/**
 * SoleadLab 2.0 — IV Import Wizard
 *
 * Steps: File Upload → Sheet Select → Device → Mapping → Preview → Import
 */

import { useState, useRef, useMemo, useEffect } from 'react';
import { useIVStore } from '@/stores/ivStore';
import type { IVColumnMapping } from '@/types/iv';
import * as ivApi from '@/services/ivApi';
import {
  parseFile,
  reparseWithHeaderRow,
  parseTransposed,
  validateMapping,
  transformRows,
  detectBlocks,
  parseBlockMeasurements,
  transformBlockRows,
  validateBlockMapping,
  extractSessionMetadata,
  parseByLabelMatching,
  extractSessionMetadataByLabels,
  IV_METRIC_FIELDS,
  type ParseResult,
  type ColumnMapping,
  type BlockMapping,
  type BlockDetectionResult,
  type ValidationWarning,
} from './ivParser';
import {
  detectAdapter,
  IV_DEVICE_ADAPTERS,
  MANUAL_COLUMN,
  MANUAL_BLOCK,
  type IVDeviceAdapter,
} from './ivAdapters';
import IVColumnMapper from './IVColumnMapper';
import IVBlockMapper from './IVBlockMapper';
import type { ImportPayload } from '@/types/iv';

interface Props {
  sessionId: string;
}

type Step = 'upload' | 'sheet' | 'device' | 'mapping' | 'preview' | 'done';

export default function IVImportWizard({ sessionId }: Props) {
  const importMeasurements = useIVStore((s) => s.importMeasurements);
  const sessions = useIVStore((s) => s.sessions);
  const session = sessions.find((s) => s.id === sessionId);

  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [transposed, setTransposed] = useState(false);
  const [mapping, setMapping] = useState<ColumnMapping>({ label: '' });
  const [warnings, setWarnings] = useState<ValidationWarning[]>([]);
  const [mappingErrors, setMappingErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; warnings: string[] } | null>(null);
  const [error, setError] = useState('');

  // Block template mode state
  const [blockMode, setBlockMode] = useState(false);
  const [blockSize, setBlockSize] = useState(1);
  const [blockFirstRow, setBlockFirstRow] = useState(0);
  const [blockCellMapping, setBlockCellMapping] = useState<BlockMapping>({});
  const [blockDetected, setBlockDetected] = useState<BlockDetectionResult | null>(null);

  // Device adapter state
  const [detectedAdapter, setDetectedAdapter] = useState<IVDeviceAdapter | null>(null);
  const [selectedAdapter, setSelectedAdapter] = useState<IVDeviceAdapter | null>(null);
  // Label-matching mode: skip manual mapping, parse directly from label text
  const [labelMode, setLabelMode] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  // Tracks whether we came from single-sheet (to decide Back target from device step)
  const [singleSheet, setSingleSheet] = useState(true);

  // Past imports (iv_column_mappings for this session)
  const [pastImports, setPastImports] = useState<IVColumnMapping[]>([]);
  const [reImporting, setReImporting] = useState(false);

  useEffect(() => {
    ivApi.listImports(sessionId).then(setPastImports).catch(() => {});
  }, [sessionId]);

  // ── Step 1: File Upload ──────────────────────────────────────────────────────
  const handleFile = async (file: File) => {
    setError('');
    try {
      const result = await parseFile(file);
      setParsed(result);
      setSelectedSheet(0);
      setHeaderRowIndex(0);
      setTransposed(false);
      setMapping({ label: '' });
      setBlockMode(false);
      setBlockCellMapping({});
      setWarnings([]);
      setMappingErrors([]);

      const rawRows = result.sheets[0]?.rawRows ?? [];

      // Block auto-detection (used if user goes manual block mode)
      const detBlocks = detectBlocks(rawRows);
      setBlockDetected(detBlocks);
      setBlockSize(detBlocks.blockSize);
      setBlockFirstRow(detBlocks.firstRow);

      // Device auto-detection
      const detDev = detectAdapter(rawRows, file.name);
      setDetectedAdapter(detDev);
      setSelectedAdapter(detDev);  // pre-select detected

      const multi = result.sheets.length > 1;
      setSingleSheet(!multi);
      setStep(multi ? 'sheet' : 'device');
    } catch (err) {
      setError(`Failed to parse file: ${err}`);
    }
  };

  // ── Step 2b: Use selected adapter ────────────────────────────────────────────
  const handleUseAdapter = (adapter: IVDeviceAdapter) => {
    const rawRows = currentSheet?.rawRows ?? [];

    if (adapter.labelMapping) {
      // Label-matching mode: re-detect blocks for the current sheet, skip mapping step
      const det = detectBlocks(rawRows);
      setBlockDetected(det);
      setBlockSize(det.blockSize);
      setBlockFirstRow(det.firstRow);
      setBlockMode(false);
      setLabelMode(true);
      setMappingErrors([]);
      setStep('preview');
      return;
    }

    setLabelMode(false);
    if (adapter.mode === 'block' && adapter.blockConfig) {
      setBlockMode(true);
      setBlockSize(adapter.blockConfig.blockSize);
      setBlockFirstRow(adapter.blockConfig.firstRow);
      setBlockCellMapping({ ...adapter.blockConfig.cellMapping });
    } else if (adapter.id === 'manual-block') {
      // Re-detect block structure for the current sheet
      const det = detectBlocks(rawRows);
      setBlockDetected(det);
      setBlockSize(det.blockSize);
      setBlockFirstRow(det.firstRow);
      setBlockCellMapping({});
      setBlockMode(true);
    } else {
      setBlockMode(false);
      const hri = adapter.columnConfig?.headerRowIndex ?? 0;
      setHeaderRowIndex(hri);
      setTransposed(!!adapter.columnConfig?.transposed);
      setMapping({ label: '', ...(adapter.columnConfig?.columnMapping ?? {}) } as ColumnMapping);
    }
    setMappingErrors([]);
    setStep('mapping');
  };

  // ── Step 3: Validate mapping ─────────────────────────────────────────────────
  const handleMappingNext = () => {
    if (blockMode) {
      const { valid, errors, warnings: w } = validateBlockMapping(blockCellMapping);
      setMappingErrors(errors);
      setWarnings(w);
      if (valid) setStep('preview');
    } else {
      const { valid, errors, warnings: w } = validateMapping(mapping);
      setMappingErrors(errors);
      setWarnings(w);
      if (valid) setStep('preview');
    }
  };

  // Enter block mode manually from column mapper
  const handleEnterBlockMode = () => {
    const rawRows = currentSheet?.rawRows ?? [];
    const det = detectBlocks(rawRows);
    setBlockDetected(det);
    setBlockSize(det.blockSize);
    setBlockFirstRow(det.firstRow);
    setBlockCellMapping({});
    setMappingErrors([]);
    setBlockMode(true);
  };

  // ── Step 4: Import ───────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!parsed) return;
    setImporting(true);
    setError('');

    try {
      let measurements;
      let transformWarns: ValidationWarning[] = [];
      let rawPayload;
      let fileMetadata: Record<string, string | number> | undefined;

      if (labelMode && selectedAdapter?.labelMapping) {
        const rawRows = currentSheet?.rawRows ?? [];
        measurements = parseByLabelMatching(rawRows, blockSize, blockFirstRow, selectedAdapter.labelMapping);
        transformWarns = [];
        rawPayload = rawRows.slice(0, 5); // lightweight reference
        if (selectedAdapter.labelMapping.sessionLabels) {
          fileMetadata = extractSessionMetadataByLabels(rawRows, blockSize, blockFirstRow, selectedAdapter.labelMapping.sessionLabels);
        }
      } else if (blockMode) {
        const rawRows = currentSheet?.rawRows ?? [];
        const extrasMapping = selectedAdapter?.extrasMapping;
        const blockRows = parseBlockMeasurements(rawRows, blockSize, blockFirstRow, blockCellMapping, extrasMapping);
        const result = await transformBlockRows(blockRows);
        measurements = result.measurements;
        transformWarns = result.warnings;
        rawPayload = blockRows;
        // Extract session-level metadata from first block if adapter provides sessionFields
        if (selectedAdapter?.sessionFields?.length) {
          fileMetadata = extractSessionMetadata(rawRows, blockSize, blockFirstRow, selectedAdapter.sessionFields);
        }
      } else {
        const result = await transformRows(activeRows, mapping);
        measurements = result.measurements;
        transformWarns = result.warnings;
        rawPayload = activeRows;
      }

      setWarnings((prev) => [...prev, ...transformWarns]);

      if (measurements.length === 0) {
        setError('No valid measurements to import (all rows had empty labels)');
        setImporting(false);
        return;
      }

      const payload: ImportPayload = {
        file_name: parsed.fileName,
        file_type: parsed.fileType,
        column_mapping: labelMode
          ? { _mode: 'label', adapter: selectedAdapter?.id ?? '' }
          : blockMode
          ? { _mode: 'block', blockSize: String(blockSize), blockFirstRow: String(blockFirstRow), ...Object.fromEntries(Object.entries(blockCellMapping).map(([k, v]) => [k, v ? `R${v.row}C${v.col}` : ''])) }
          : mapping as unknown as Record<string, string>,
        raw_payload_json: rawPayload,
        measurements,
        ...(fileMetadata ? { file_metadata: fileMetadata } : {}),
      };

      const result = await importMeasurements(sessionId, payload);
      setImportResult({
        imported: result.imported_count,
        skipped: result.skipped_count,
        warnings: result.warnings,
      });
      setStep('done');
    } catch (err) {
      setError(String(err));
    } finally {
      setImporting(false);
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────
  const currentSheet = parsed?.sheets[selectedSheet];
  const { headers, rows: activeRows } = useMemo(() => {
    if (!currentSheet) return { headers: [] as string[], rows: [] as Record<string, unknown>[] };
    if (transposed) return parseTransposed(currentSheet.rawRows);
    if (headerRowIndex === 0) return { headers: currentSheet.headers, rows: currentSheet.rows };
    return reparseWithHeaderRow(currentSheet.rawRows, headerRowIndex);
  }, [currentSheet, headerRowIndex, transposed]);
  const previewRows = activeRows.slice(0, 5);

  // ── Re-import from stored source file ────────────────────────────────────────
  const handleReImport = async () => {
    if (!session?.source_file_r2_key) return;
    setReImporting(true);
    setError('');
    try {
      const blob = await ivApi.downloadSourceFile(sessionId);
      const fileName = session.source_file_name ?? 'source.xlsx';
      const file = new File([blob], fileName, { type: blob.type });
      await handleFile(file);
    } catch (err) {
      setError(`Failed to load source file: ${err}`);
    } finally {
      setReImporting(false);
    }
  };

  // ── Confidence display helpers ───────────────────────────────────────────────
  const confidencePct = (adapter: IVDeviceAdapter | null): number | null => {
    if (!adapter || !detectedAdapter || adapter.id !== detectedAdapter.id) return null;
    const raw = adapter.detect(currentSheet?.rawRows ?? [], parsed?.fileName ?? '');
    return Math.round(raw * 100);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {error && <div className="iv-note iv-note--error">{error}</div>}

      {/* ── Step 1: Upload ───────────────────────────────────────── */}
      {step === 'upload' && (
        <div className="iv-wizard-step">

          {/* Previously imported files */}
          {(session?.source_file_r2_key || session?.source_file_name || pastImports.length > 0) && (
            <div className="iv-stored-files">
              <div className="iv-stored-files__title">Previously Imported</div>

              {(session?.source_file_r2_key || session?.source_file_name) && (
                <div className="iv-stored-file-card">
                  <div className="iv-stored-file-card__body">
                    <span className="iv-stored-file-card__icon">📄</span>
                    <div>
                      <div className="iv-stored-file-card__name">{session?.source_file_name ?? 'Source file'}</div>
                      <div className="iv-stored-file-card__meta">Stored at session creation</div>
                    </div>
                  </div>
                  {session?.source_file_r2_key && (
                    <button
                      className="iv-btn iv-btn--sm"
                      disabled={reImporting}
                      onClick={handleReImport}
                      title="Fetch this file from storage and restart the import wizard"
                    >
                      {reImporting ? 'Loading…' : '↩ Re-import'}
                    </button>
                  )}
                </div>
              )}

              {pastImports.map((imp) => {
                const modeInfo = (() => {
                  try {
                    const m = JSON.parse(imp.mapping_json) as Record<string, string>;
                    if (m._mode === 'label') return `Auto (${m.adapter ?? 'device'})`;
                    if (m._mode === 'block') return 'Block template';
                    return 'Column mapping';
                  } catch { return ''; }
                })();
                return (
                  <div key={imp.id} className="iv-stored-file-card iv-stored-file-card--history">
                    <div className="iv-stored-file-card__body">
                      <span className="iv-stored-file-card__icon">📋</span>
                      <div>
                        <div className="iv-stored-file-card__name">{imp.file_name}</div>
                        <div className="iv-stored-file-card__meta">
                          {new Date(imp.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                          {modeInfo && ` · ${modeInfo}`}
                        </div>
                      </div>
                    </div>
                    {session?.source_file_r2_key && (
                      <button
                        className="iv-btn iv-btn--sm"
                        disabled={reImporting}
                        onClick={handleReImport}
                        title="Fetch the stored source file and restart the import wizard"
                      >
                        {reImporting ? 'Loading…' : '↩ Re-import'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="iv-wizard-step__title" style={{ marginTop: (session?.source_file_name || pastImports.length > 0) ? 16 : 0 }}>
            Upload IV Measurement File
          </div>
          <div
            className="iv-file-drop"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) handleFile(f);
            }}
          >
            <div style={{ fontSize: 'var(--sl-text-lg)', marginBottom: 8 }}>Drop file here or click to browse</div>
            <div style={{ fontSize: 'var(--sl-text-xs)', color: 'var(--text-muted)' }}>Supports CSV, XLS, XLSX</div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </div>
      )}

      {/* ── Step 2: Sheet Selection ──────────────────────────────── */}
      {step === 'sheet' && parsed && (
        <div className="iv-wizard-step">
          <div className="iv-wizard-step__title">Select Sheet</div>
          {parsed.sheets.map((sheet, i) => (
            <div
              key={i}
              className={`iv-session-card${selectedSheet === i ? ' iv-session-card--active' : ''}`}
              onClick={() => setSelectedSheet(i)}
              style={{ cursor: 'pointer' }}
            >
              <div className="iv-session-card__name">{sheet.name}</div>
              <div className="iv-session-card__meta">{sheet.rows.length} rows, {sheet.headers.length} columns</div>
            </div>
          ))}
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="iv-btn" onClick={() => setStep('upload')}>Back</button>
            <button className="iv-btn iv-btn--primary" onClick={() => {
              // Re-detect blocks for the newly selected sheet
              const rawRows = parsed.sheets[selectedSheet]?.rawRows ?? [];
              const det = detectBlocks(rawRows);
              setBlockDetected(det);
              setBlockSize(det.blockSize);
              setBlockFirstRow(det.firstRow);
              // Re-run adapter detection on the selected sheet
              const det2 = detectAdapter(rawRows, parsed.fileName);
              setDetectedAdapter(det2);
              setSelectedAdapter(det2);
              setStep('device');
            }}>Next</button>
          </div>
        </div>
      )}

      {/* ── Step 3: Device Selection ─────────────────────────────── */}
      {step === 'device' && (
        <div className="iv-wizard-step">
          <div className="iv-wizard-step__title">Select Measurement Device</div>

          {detectedAdapter && (
            <div className="iv-device-detected-banner">
              ✦ Auto-detected: <strong>{detectedAdapter.manufacturer} {detectedAdapter.name}</strong>
              {(() => { const pct = confidencePct(detectedAdapter); return pct !== null ? <span className="iv-device-detected-banner__pct">{pct}% confidence</span> : null; })()}
            </div>
          )}
          {!detectedAdapter && (
            <div className="iv-note iv-note--info" style={{ fontSize: 'var(--sl-text-xs)', marginBottom: 8 }}>
              No device auto-detected. Select your device below or use a manual mapping option.
            </div>
          )}

          {/* Known devices */}
          <div className="iv-device-picker">
            {IV_DEVICE_ADAPTERS.map((adapter) => {
              const isSelected = selectedAdapter?.id === adapter.id;
              const isDetected = detectedAdapter?.id === adapter.id;
              return (
                <div
                  key={adapter.id}
                  className={`iv-device-card${isSelected ? ' iv-device-card--selected' : ''}${isDetected ? ' iv-device-card--detected' : ''}`}
                  onClick={() => setSelectedAdapter(adapter)}
                >
                  {isDetected && <span className="iv-device-card__badge">Detected</span>}
                  <div className="iv-device-card__icon">{adapter.icon}</div>
                  <div className="iv-device-card__name">{adapter.name}</div>
                  <div className="iv-device-card__mfr">{adapter.manufacturer}</div>
                  <div className="iv-device-card__mode">{adapter.mode === 'block' ? 'Block' : 'Column'}</div>
                </div>
              );
            })}
          </div>

          {/* Manual override section */}
          <div className="iv-device-section-label">Manual Override</div>
          <div className="iv-device-picker">
            {[MANUAL_COLUMN, MANUAL_BLOCK].map((adapter) => {
              const isSelected = selectedAdapter?.id === adapter.id;
              return (
                <div
                  key={adapter.id}
                  className={`iv-device-card iv-device-card--manual${isSelected ? ' iv-device-card--selected' : ''}`}
                  onClick={() => setSelectedAdapter(adapter)}
                >
                  <div className="iv-device-card__icon">{adapter.icon}</div>
                  <div className="iv-device-card__name">{adapter.name}</div>
                  <div className="iv-device-card__mfr">{adapter.description}</div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="iv-btn" onClick={() => setStep(singleSheet ? 'upload' : 'sheet')}>Back</button>
            <button
              className="iv-btn iv-btn--primary"
              disabled={!selectedAdapter}
              onClick={() => selectedAdapter && handleUseAdapter(selectedAdapter)}
            >
              Use Selected →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Column Mapping ───────────────────────────────── */}
      {step === 'mapping' && (
        <div className="iv-wizard-step">
          <div className="iv-wizard-step__title">
            {selectedAdapter && selectedAdapter.id !== 'manual-column' && selectedAdapter.id !== 'manual-block'
              ? <>Review Mapping — <span style={{ fontWeight: 400 }}>{selectedAdapter.manufacturer} {selectedAdapter.name}</span></>
              : 'Map Columns'
            }
          </div>

          {blockMode ? (
            <IVBlockMapper
              rawRows={currentSheet?.rawRows ?? []}
              blockSize={blockSize}
              blockFirstRow={blockFirstRow}
              blockCellMapping={blockCellMapping}
              blockDetected={blockDetected}
              errors={mappingErrors}
              onBlockSizeChange={setBlockSize}
              onBlockFirstRowChange={setBlockFirstRow}
              onBlockCellMappingChange={setBlockCellMapping}
              onExitBlockMode={() => { setBlockMode(false); setMappingErrors([]); }}
            />
          ) : (
            <IVColumnMapper
              rawRows={currentSheet?.rawRows ?? []}
              headers={headers}
              previewRows={previewRows}
              headerRowIndex={headerRowIndex}
              transposed={transposed}
              mapping={mapping}
              errors={mappingErrors}
              onHeaderRowChange={(i) => { setHeaderRowIndex(i); setMapping({ label: '' }); }}
              onTransposedChange={(v) => { setTransposed(v); setMapping({ label: '' }); }}
              onMappingChange={setMapping}
              onBlockModeSelect={handleEnterBlockMode}
            />
          )}

          {warnings.length > 0 && (
            <div className="iv-note" style={{ marginTop: 8 }}>
              {warnings.map((w, i) => <div key={i}>{w.message}</div>)}
            </div>
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="iv-btn" onClick={() => { setStep('device'); setMappingErrors([]); }}>Back</button>
            <button className="iv-btn iv-btn--primary" onClick={handleMappingNext}>Preview →</button>
          </div>
        </div>
      )}

      {/* ── Step 5: Preview ──────────────────────────────────────── */}
      {step === 'preview' && (() => {
        const rawRows = currentSheet?.rawRows ?? [];

        // Label-matched preview
        const labelPreviewRows = labelMode && selectedAdapter?.labelMapping
          ? parseByLabelMatching(rawRows, blockSize, blockFirstRow, selectedAdapter.labelMapping).slice(0, 5)
          : null;

        const blockPreviewRows = !labelMode && blockMode
          ? parseBlockMeasurements(rawRows, blockSize, blockFirstRow, blockCellMapping).slice(0, 5)
          : null;

        const mappedFields = IV_METRIC_FIELDS.filter((f) =>
          labelMode
            ? !!(selectedAdapter?.labelMapping?.fieldLabels as Record<string, unknown> | undefined)?.[f.key]
            : blockMode
            ? !!blockCellMapping[f.key]
            : !!(mapping as Record<string, string>)[f.key]
        );

        const totalCount = (labelMode || blockMode)
          ? Math.max(0, Math.floor((rawRows.length - blockFirstRow) / blockSize))
          : activeRows.length;

        return (
          <div className="iv-wizard-step">
            <div className="iv-wizard-step__title">Preview (first 5 measurements)</div>

            {labelMode && (
              <div className="iv-note iv-note--info" style={{ fontSize: 'var(--sl-text-xs)', marginBottom: 8 }}>
                Auto label matching — {selectedAdapter?.manufacturer} {selectedAdapter?.name}.
                Detected block size: <strong>{blockSize}</strong> rows. Estimated <strong>{totalCount}</strong> measurements.
              </div>
            )}
            {!labelMode && blockMode && (
              <div className="iv-note iv-note--info" style={{ fontSize: 'var(--sl-text-xs)', marginBottom: 8 }}>
                Block template: {blockSize} rows per measurement, starting at row {blockFirstRow + 1}.
                Estimated <strong>{totalCount}</strong> measurements.
              </div>
            )}

            {warnings.length > 0 && (
              <div className="iv-note" style={{ marginBottom: 8 }}>
                {warnings.map((w, i) => <div key={i}>{w.message}</div>)}
              </div>
            )}

            <div className="iv-preview-table">
              <table className="iv-table">
                <thead>
                  <tr>
                    {mappedFields.map((f) => <th key={f.key}>{f.label}</th>)}
                    {labelMode && <th>Result</th>}
                  </tr>
                </thead>
                <tbody>
                  {labelMode
                    ? (labelPreviewRows ?? []).map((row, i) => (
                        <tr key={i}>
                          {mappedFields.map((f) => (
                            <td key={f.key}>{(row as unknown as Record<string, unknown>)[f.key] != null ? String((row as unknown as Record<string, unknown>)[f.key]) : ''}</td>
                          ))}
                          <td style={{ color: row.result?.includes('Not Ok') ? 'var(--orange)' : 'var(--green)', fontSize: 'var(--sl-text-xs)', fontFamily: 'var(--font-mono)' }}>
                            {row.result ?? ''}
                          </td>
                        </tr>
                      ))
                    : blockMode
                    ? (blockPreviewRows ?? []).map((row, i) => (
                        <tr key={i}>
                          {mappedFields.map((f) => (
                            <td key={f.key}>{row[f.key] != null ? String(row[f.key]) : ''}</td>
                          ))}
                        </tr>
                      ))
                    : previewRows.map((row, i) => (
                        <tr key={i}>
                          {mappedFields.map((f) => {
                            const col = (mapping as Record<string, string>)[f.key];
                            return <td key={f.key}>{col ? String(row[col] ?? '') : ''}</td>;
                          })}
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="iv-btn" onClick={() => setStep(labelMode ? 'device' : 'mapping')}>Back</button>
              <button className="iv-btn iv-btn--primary" onClick={handleImport} disabled={importing}>
                {importing ? 'Importing...' : `Import ${totalCount} measurements`}
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Step 6: Done ─────────────────────────────────────────── */}
      {step === 'done' && importResult && (
        <div className="iv-wizard-step">
          <div className="iv-note iv-note--success">
            Successfully imported {importResult.imported} measurement(s).
            {importResult.skipped > 0 && ` ${importResult.skipped} duplicate(s) skipped.`}
          </div>
          {importResult.warnings.length > 0 && (
            <div className="iv-note">
              {importResult.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
          <button
            className="iv-btn iv-btn--primary"
            style={{ marginTop: 12 }}
            onClick={() => {
              setStep('upload');
              setParsed(null);
              setMapping({ label: '' });
              setWarnings([]);
              setImportResult(null);
              setBlockMode(false);
              setLabelMode(false);
              setBlockCellMapping({});
              setBlockDetected(null);
              setDetectedAdapter(null);
              setSelectedAdapter(null);
              // Refresh import history
              ivApi.listImports(sessionId).then(setPastImports).catch(() => {});
            }}
          >
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
}
